import {
  DOCTYPE_FEATURES,
  isDoctypeFeature,
  type DoctypeFeature,
} from "../schemas/doctype-registry";

// ============================================================================
// Doctype Admin Epic (#347) A4 (#348) — pure tab-selection helpers.
//
// WHY THESE LIVE IN THE CORE MODULE (not in apps/web):
// ---------------------------------------------------------------------------
//   These are pure data helpers with zero React / DOM / icon deps. Their
//   contract is driven by the `DoctypeFeature` union (also owned by this
//   module's schema). Co-locating them here means:
//     1. The UI layer (apps/web) can treat them as a stable SDK surface
//        rather than forking per-page logic.
//     2. They are unit-testable from the vitest setup already present in
//        modules/core — no need to introduce a vitest config in apps/web
//        just for tab-selection tests.
//     3. If any other admin surface ever needs to render the same feature
//        tabs (e.g. a dev-tools inspector), it gets the same behaviour
//        for free.
//
// The presentational config (icons, Thai labels, Placeholder components)
// stays in apps/web to keep module-core free of React/lucide imports.
// ============================================================================

/**
 * Return the subset of `features` that are valid `DoctypeFeature` members,
 * preserving input order and removing duplicates.
 *
 * Rules:
 *   - Unknown strings are dropped silently (version-skew tolerance — an
 *     older frontend talking to a newer backend degrades gracefully
 *     instead of crashing on an unknown capability chip).
 *   - Duplicates keep their first occurrence.
 *   - Input is treated as `readonly string[]` so callers can pass a
 *     registry-parsed array without having to cast to DoctypeFeature.
 */
export function resolveDoctypeFeatures(
  features: readonly string[],
): DoctypeFeature[] {
  const seen = new Set<DoctypeFeature>();
  const result: DoctypeFeature[] = [];
  for (const value of features) {
    if (!isDoctypeFeature(value)) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

/** Outcome of `pickInitialTab` — used by the detail page to both pick the
 *  active tab and decide whether to scrub a stale `?tab=` from the URL. */
export interface PickInitialTabResult {
  /** The key of the tab to render active, or `null` if the doctype has no
   *  features at all (caller renders a "nothing to configure" empty state). */
  activeKey: DoctypeFeature | null;
  /** True when the URL had a `?tab=` value we couldn't honour (unknown
   *  feature, or feature not declared by this doctype). Caller should
   *  `replace` the URL to drop the bad param. */
  shouldSyncUrl: boolean;
}

/**
 * Given a doctype's `features` array and the URL's `?tab=` value (or null),
 * decide which tab should be active AND whether the URL needs to be fixed.
 *
 * Algorithm (ERPNext-style — all tabs visible, unsupported ones rendered
 * with a "ไม่รองรับ" panel):
 *   1. Filter `features` through `resolveDoctypeFeatures`. If empty →
 *      `{ activeKey: null, shouldSyncUrl: false }`. Caller renders an
 *      "no admin features" message; the doctype opted out of the full
 *      Doctype Admin surface entirely.
 *   2. Compute `firstSupportedKey` — the first SUPPORTED feature. This is
 *      the default active tab. Showing an unsupported tab by default
 *      would land the user on a "doesn't work here" message, which is
 *      confusing.
 *   3. If `urlTab` is null or "":
 *        → active = firstSupportedKey, shouldSyncUrl = false
 *      Bare URL, back button returns to list.
 *   4. If `urlTab` is a valid DoctypeFeature (in TAB_CONFIG, regardless
 *      of whether this doctype declared it):
 *        → active = urlTab, shouldSyncUrl = false
 *      The caller renders the unsupported panel if `urlTab` isn't in
 *      `features`. This lets share links to `?tab=workflow` work across
 *      any doctype.
 *   5. Otherwise (urlTab is NOT a known DoctypeFeature name):
 *        → active = firstSupportedKey, shouldSyncUrl = true
 *      Scrub the garbage value so refresh/share stays sane.
 *
 * Pure — no React, no side effects, no I/O.
 */
export function pickInitialTab(
  features: readonly string[],
  urlTab: string | null,
): PickInitialTabResult {
  const supported = resolveDoctypeFeatures(features);
  if (supported.length === 0) {
    // Doctype opted out — no admin surface at all.
    return { activeKey: null, shouldSyncUrl: false };
  }
  const firstSupportedKey = supported[0]!;

  if (urlTab === null || urlTab === "") {
    return { activeKey: firstSupportedKey, shouldSyncUrl: false };
  }

  // Any valid DoctypeFeature is honored — supported or not. The page
  // renders a "feature not supported for this doctype" panel instead of
  // the real editor when it's not in `features`.
  if (isDoctypeFeature(urlTab)) {
    return { activeKey: urlTab, shouldSyncUrl: false };
  }

  // Unknown value — not a feature at all. Scrub.
  return { activeKey: firstSupportedKey, shouldSyncUrl: true };
}

// Re-export the const + union so consumers of this helper can import the
// full tab surface from one place without reaching into `../schemas/*`.
// Mirrors the shape of other @kubdee/sdk re-exports.
export { DOCTYPE_FEATURES, type DoctypeFeature };
